import React, { useRef } from 'react';
import { Box, Paper, CircularProgress, Alert, Snackbar, Tooltip, IconButton } from '@mui/material';
import { PhotoCamera as PhotoCameraIcon, Edit as EditIcon } from '@mui/icons-material';
import { BRAND_COLORS, alpha } from '../../../styles/theme';
import { useDashboardStore } from '../../../stores/dashboardStore';
import { useDronePictureUrlQuery } from './dashboard.queries';
import { useUploadDronePictureMutation } from './dashboard.mutations';

interface DroneProfilePictureProps {
  droneSerial: string;
  pictureUrl?: string | null;
  onPictureUpdate?: (newPictureUrl: string) => void;
  onDataRefresh?: () => void;
}

export const DroneProfilePicture: React.FC<DroneProfilePictureProps> = ({
  droneSerial,
  pictureUrl,
  onPictureUpdate,
  onDataRefresh,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: actualPictureUrl = null, isLoading: isLoadingPicture } = useDronePictureUrlQuery(
    droneSerial,
    !!pictureUrl
  );

  const uploadMutation = useUploadDronePictureMutation(droneSerial);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileUpload = (file: File) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      // Inline validation — no server call needed, toast not appropriate
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return;
    }

    uploadMutation.mutate(file, {
      onSuccess: (newPictureUrl) => {
        // Refresh overview data to ensure fresh data across the app
        useDashboardStore.getState().refreshOverviewData();
        onPictureUpdate?.(newPictureUrl);
        onDataRefresh?.();
        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      },
      onError: () => {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      },
    });
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const isUploading = uploadMutation.isPending;

  return (
    <Box>
      <Paper
        sx={{
          p: 0,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 2,
          textAlign: 'center',
          backgroundColor: 'background.paper',
          height: '100%',
          maxHeight: { xs: 250, sm: 300, md: 350, lg: 400 },
          minHeight: { xs: 200, sm: 220, md: 250 },
          overflow: 'hidden',
        }}
      >
        {/* Picture Display Area */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.1),
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            {actualPictureUrl && !isLoadingPicture && (
              <img
                src={actualPictureUrl}
                alt={`Drone ${droneSerial}`}
                onError={(e) => {
                  console.error(
                    '🖼️ [DroneProfilePicture] Image load error for URL:',
                    actualPictureUrl,
                    'Error:',
                    e
                  );
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}
            {!actualPictureUrl && !isLoadingPicture && (
              <PhotoCameraIcon
                sx={{
                  fontSize: { xs: 60, sm: 70, md: 80, lg: 90 },
                  color: alpha(BRAND_COLORS.forestGreen.main, 0.6),
                }}
              />
            )}
            {isLoadingPicture && (
              <CircularProgress size={60} sx={{ color: BRAND_COLORS.forestGreen.main }} />
            )}
          </Box>

          {/* Edit overlay */}
          <Tooltip title="Upload new picture">
            <IconButton
              onClick={handleUploadClick}
              disabled={isUploading}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: BRAND_COLORS.forestGreen.main,
                color: 'white',
                width: 36,
                height: 36,
                '&:hover': {
                  backgroundColor: '#002B20',
                },
                '&.Mui-disabled': {
                  backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.3),
                },
              }}
            >
              {isUploading ? (
                <CircularProgress size={18} sx={{ color: 'white' }} />
              ) : (
                <EditIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </Paper>

      {/* Upload success notification */}
      <Snackbar
        open={uploadMutation.isSuccess}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success">Picture uploaded successfully!</Alert>
      </Snackbar>
    </Box>
  );
};
